
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { QCOrder, QCInspection, QCChecklist, QCDashboardStats, QCRecentAction } from '../types';

export const useQC = () => {
  const queryClient = useQueryClient();

  // Dashboard data
  const dashboard = useQuery({
    queryKey: ['qc', 'dashboard'],
    queryFn: async () => {
      // Mock data for development
      const stats: QCDashboardStats = {
        pending: 12,
        completed: 45,
        rejected: 3,
        approvalRate: 93.8,
        avgInspectionTime: 25,
        todayCompleted: 8
      };

      const recent: QCRecentAction[] = [
        { orderId: 'ORD-2024-001', product: 'Gold Ring 18K', status: 'approved', timestamp: '2 hours ago', inspector: 'John Doe' },
        { orderId: 'ORD-2024-002', product: 'Diamond Necklace', status: 'rejected', timestamp: '3 hours ago', inspector: 'Jane Smith' },
        { orderId: 'ORD-2024-003', product: 'Silver Bracelet', status: 'approved', timestamp: '4 hours ago', inspector: 'Mike Johnson' },
        { orderId: 'ORD-2024-004', product: 'Gold Earrings', status: 'in-progress', timestamp: '5 hours ago', inspector: 'Sarah Wilson' },
        { orderId: 'ORD-2024-005', product: 'Diamond Ring', status: 'approved', timestamp: '6 hours ago', inspector: 'John Doe' }
      ];

      return { stats, recent };
    }
  });

  // QC Orders list
  const orders = useQuery({
    queryKey: ['qc', 'orders'],
    queryFn: async () => {
      // Mock data for development
      const orders: QCOrder[] = [
        {
          id: '1',
          orderNo: 'ORD-2024-001',
          product: 'Gold Ring 18K - Custom Design',
          customer: 'Rajesh Kumar',
          vendor: 'Golden Crafts Ltd',
          status: 'pending',
          priority: 'high',
          createdAt: '2024-01-15T10:30:00Z',
          dueDate: '2024-01-20T18:00:00Z'
        },
        {
          id: '2',
          orderNo: 'ORD-2024-002',
          product: 'Diamond Necklace Set',
          customer: 'Priya Sharma',
          vendor: 'Diamond Palace',
          status: 'in-progress',
          priority: 'medium',
          createdAt: '2024-01-14T14:20:00Z',
          dueDate: '2024-01-22T18:00:00Z',
          assignedTo: 'John Doe'
        },
        {
          id: '3',
          orderNo: 'ORD-2024-003',
          product: 'Silver Bracelet with Gemstones',
          customer: 'Amit Patel',
          vendor: 'Silver Works Co',
          status: 'approved',
          priority: 'low',
          createdAt: '2024-01-13T09:15:00Z',
          dueDate: '2024-01-25T18:00:00Z'
        },
        {
          id: '4',
          orderNo: 'ORD-2024-004',
          product: 'Gold Earrings - Traditional',
          customer: 'Sunita Reddy',
          vendor: 'Heritage Jewels',
          status: 'rejected',
          priority: 'medium',
          createdAt: '2024-01-12T16:45:00Z',
          dueDate: '2024-01-18T18:00:00Z'
        },
        {
          id: '5',
          orderNo: 'ORD-2024-005',
          product: 'Diamond Ring - Engagement',
          customer: 'Vikram Singh',
          vendor: 'Royal Diamonds',
          status: 'pending',
          priority: 'high',
          createdAt: '2024-01-16T11:00:00Z',
          dueDate: '2024-01-21T18:00:00Z'
        }
      ];
      return orders;
    }
  });

  // Get single order for inspection
  const getOrder = (id: string) => useQuery({
    queryKey: ['qc', 'order', id],
    queryFn: async () => {
      // Mock data for development
      const order: QCOrder = {
        id,
        orderNo: 'ORD-2024-001',
        product: 'Gold Ring 18K - Custom Design',
        customer: 'Rajesh Kumar',
        vendor: 'Golden Crafts Ltd',
        status: 'pending',
        priority: 'high',
        createdAt: '2024-01-15T10:30:00Z',
        dueDate: '2024-01-20T18:00:00Z'
      };
      return order;
    }
  });

  // Get inspection details
  const getInspection = (orderId: string) => useQuery({
    queryKey: ['qc', 'inspection', orderId],
    queryFn: async () => {
      // Mock data for development
      const inspection: QCInspection = {
        id: '1',
        orderId,
        checklistId: 'checklist-gold',
        inspectorId: 'inspector-1',
        status: 'in-progress',
        startedAt: '2024-01-16T09:00:00Z',
        notes: '',
        photos: [],
        checklistItems: [
          {
            id: '1',
            description: 'Check gold purity (18K verification)',
            category: 'Material Quality',
            isRequired: true,
            status: 'pending'
          },
          {
            id: '2',
            description: 'Verify weight accuracy',
            category: 'Specifications',
            isRequired: true,
            status: 'pending'
          },
          {
            id: '3',
            description: 'Inspect surface finish and polish',
            category: 'Craftsmanship',
            isRequired: true,
            status: 'pending'
          },
          {
            id: '4',
            description: 'Check for structural integrity',
            category: 'Durability',
            isRequired: true,
            status: 'pending'
          },
          {
            id: '5',
            description: 'Verify design specifications match order',
            category: 'Design Compliance',
            isRequired: true,
            status: 'pending'
          }
        ]
      };
      return inspection;
    }
  });

  // Get checklists for management
  const checklists = useQuery({
    queryKey: ['qc', 'checklists'],
    queryFn: async () => {
      // Mock data for development
      const checklists: QCChecklist[] = [
        {
          id: 'checklist-gold',
          name: 'Gold Jewelry Quality Check',
          description: 'Standard quality control checklist for gold jewelry items',
          category: 'gold',
          isActive: true,
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-15T10:00:00Z',
          items: [
            {
              id: '1',
              description: 'Check gold purity (18K/22K verification)',
              category: 'Material Quality',
              isRequired: true
            },
            {
              id: '2',
              description: 'Verify weight accuracy (±0.1g tolerance)',
              category: 'Specifications',
              isRequired: true
            },
            {
              id: '3',
              description: 'Inspect surface finish and polish quality',
              category: 'Craftsmanship',
              isRequired: true
            }
          ]
        },
        {
          id: 'checklist-diamond',
          name: 'Diamond Jewelry Quality Check',
          description: 'Comprehensive checklist for diamond jewelry verification',
          category: 'diamond',
          isActive: true,
          createdAt: '2024-01-01T00:00:00Z',
          updatedAt: '2024-01-10T15:30:00Z',
          items: [
            {
              id: '1',
              description: 'Verify diamond certification and authenticity',
              category: 'Certification',
              isRequired: true
            },
            {
              id: '2',
              description: 'Check diamond setting security',
              category: 'Setting Quality',
              isRequired: true
            }
          ]
        }
      ];
      return checklists;
    }
  });

  // Update inspection
  const updateInspection = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<QCInspection> }) => {
      return api.put(`/qc/inspections/${id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qc'] });
    }
  });

  // Complete inspection
  const completeInspection = useMutation({
    mutationFn: async ({ id, decision, notes, checklistItems }: { 
      id: string; 
      decision: 'approved' | 'rejected'; 
      notes?: string;
      checklistItems?: any[];
    }) => {
      return api.post(`/qc/inspections/${id}/complete`, { decision, notes, checklistItems });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qc'] });
    }
  });

  // Create/Update checklist
  const saveChecklist = useMutation({
    mutationFn: async (data: Partial<QCChecklist>) => {
      if (data.id) {
        return api.put(`/qc/checklists/${data.id}`, data);
      }
      return api.post('/qc/checklists', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qc', 'checklists'] });
    }
  });

  // Delete checklist
  const deleteChecklist = useMutation({
    mutationFn: async (id: string) => {
      return api.delete(`/qc/checklists/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['qc', 'checklists'] });
    }
  });

  return {
    dashboard,
    orders,
    getOrder,
    getInspection,
    checklists,
    updateInspection,
    completeInspection,
    saveChecklist,
    deleteChecklist
  };
};
